// src/pages/ConsultationRequest.jsx
// Consultation / Feature Request — Creates a SIM-T ticket

import React, { useState, useEffect } from 'react';

const requestTypes = [
    'Consultation Request (to initiate a discussion with P&S for your internal process improvement)',
    'Feature Request (to suggest any new idea/change in the existing permissions/security setup)'
];

const tenantOptions = [
    'Tenant 178', 'Tenant 290', 'Tenant 310', 'Tenant 321', 'Tenant 333', 'Tenant 398', 'Other', 'None'
];

const ConsultationRequest = ({ userAlias }) => {
    const [title, setTitle] = useState('');
    const [teamOrg, setTeamOrg] = useState('');
    const [requestType, setRequestType] = useState('');
    const [relatedTenant, setRelatedTenant] = useState('');
    const [description, setDescription] = useState('');
    const [impact, setImpact] = useState('');
    const [workaround, setWorkaround] = useState('');
    const [existingRef, setExistingRef] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(null);
    const [submitError, setSubmitError] = useState('');

    const isValid = title.trim() && requestType && relatedTenant;

    // Trigger auth check on page load to ensure token is fresh
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const { getAccessToken, refreshToken } = await import('../services/auth');
                const token = getAccessToken();
                if (!token) {
                    // No token — trigger refresh
                    if (refreshToken) await refreshToken();
                }
            } catch (e) {
                // Auth will be handled on submit
            }
        };
        checkAuth();
    }, []);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError('');
        try {
            const { getAccessToken } = await import('../services/auth');
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';
            const token = getAccessToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const descBody = [
                `## Consultation / Feature Request`,
                ``,
                `**Requester:** ${userAlias}`,
                `**Type:** ${requestType}`,
                `**Related Tenant:** ${relatedTenant}`,
                ``,
                `---`,
                `**1. Team name:** ${teamOrg || 'N/A'}`,
                ``,
                `**2. Describe the request in detail:** ${description || 'N/A'}`,
                ``,
                `**3. What would be the impact of this request? Who would it benefit?** ${impact || 'N/A'}`,
                ``,
                `**4. How are you managing the process currently / workaround?** ${workaround || 'N/A'}`,
                ``,
                `**5. Existing SIM/approval/email communication:** ${existingRef || 'None'}`,
            ].join('\n');

            const response = await fetch(`${API_BASE_URL}/validate`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    operation: 'createTicket',
                    title: `[${requestType.includes('Consultation') ? 'Consultation' : 'Feature Request'}] ${teamOrg} - ${relatedTenant}`,
                    description: descBody,
                    severity: 'SEV_5',
                    requesterAlias: userAlias
                })
            });

            const raw = await response.json();
            const result = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw;

            if (response.status === 401) {
                // Token expired — trigger re-auth
                sessionStorage.clear();
                window.location.href = '/';
                return;
            }

            if (!response.ok || result.error) {
                throw new Error(result.error || `API error: ${response.status}`);
            }

            setSubmitSuccess(result);
        } catch (err) {
            setSubmitError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitSuccess) {
        return (
            <div style={{ maxWidth: '700px', margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                <h2 style={{ color: '#166534', marginBottom: '8px' }}>Request Submitted Successfully!</h2>
                <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>Ticket ID: <strong>{submitSuccess.ticketId}</strong></p>
                {submitSuccess.ticketUrl && (
                    <a href={submitSuccess.ticketUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-block', padding: '10px 20px', borderRadius: '8px', background: '#0073bb', color: '#fff', fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>
                        View Ticket on SIM-T ↗
                    </a>
                )}
                <div style={{ marginTop: '20px' }}>
                    <button onClick={() => window.location.href = '/'} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>
                        ← Back to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '32px 24px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111', marginBottom: '4px' }}>💬 Consultation / Feature Request</h1>
            
            {/* Info Section */}
            <div style={{ padding: '16px', borderRadius: '10px', background: '#eef6ff', border: '1px solid #b8d4f0', marginBottom: '16px', fontSize: '13px', color: '#333', lineHeight: '1.6' }}>
                <p style={{ margin: '0' }}>Please use this template to initiate any consultation/new idea discussion/feature request that P&S team can help you with. Please be as detailed and use as many unique use cases to describe the issue.</p>
            </div>

            <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', marginBottom: '20px', fontSize: '12px', color: '#92400e', lineHeight: '1.6' }}>
                ⏱️ <strong>Note:</strong> Feature requests are reviewed on a quarterly basis, first response time is 3 months. Consultation requests are reviewed at a weekly level, first response time is a week however the consultation connect is based on open office hours slots.
            </div>

            {submitError && <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', fontSize: '13px', color: '#991b1b', marginBottom: '16px' }}>❌ {submitError}</div>}

            {/* Request Type */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Request Type <span style={{ color: '#d13212' }}>*</span></label>
                <select value={requestType} onChange={(e) => setRequestType(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}>
                    <option value="">— Select —</option>
                    {requestTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            {/* Related Tenant */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Related Tenant <span style={{ color: '#d13212' }}>*</span></label>
                <select value={relatedTenant} onChange={(e) => setRelatedTenant(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}>
                    <option value="">— Select —</option>
                    {tenantOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            {/* 1. Team Name */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>1. Team name: <span style={{ color: '#d13212' }}>*</span></label>
                <input type="text" value={teamOrg} onChange={(e) => setTeamOrg(e.target.value)} placeholder="e.g., APE Compliance EU"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
            </div>

            {/* 2. Describe request */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>2. Describe the request in detail: <span style={{ color: '#d13212' }}>*</span></label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Be as detailed as possible..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* 3. Impact */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>3. What would be the impact of this request? Who would it benefit (in addition to you/your team)?</label>
                <textarea value={impact} onChange={(e) => setImpact(e.target.value)} rows={3} placeholder="Describe the impact and beneficiaries..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* 4. Workaround */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>4. How are you managing the process currently and what is the workaround until the consultation/feature request is put into action?</label>
                <textarea value={workaround} onChange={(e) => setWorkaround(e.target.value)} rows={3} placeholder="Describe current process/workaround..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* 5. Existing Reference */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>5. Please attach file if there's any existing SIM/approval/email communication about this consultation/feature request:</label>
                <textarea value={existingRef} onChange={(e) => setExistingRef(e.target.value)} rows={2} placeholder="Link to SIM/approval/email..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Submit */}
            <button onClick={handleSubmit} disabled={!isValid || isSubmitting || !teamOrg.trim() || !description.trim()}
                style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: (isValid && !isSubmitting && teamOrg.trim() && description.trim()) ? 'pointer' : 'not-allowed', border: 'none', background: (isValid && teamOrg.trim() && description.trim()) ? '#0073bb' : '#ccc', color: '#fff' }}>
                {isSubmitting ? '⏳ Submitting...' : '🚀 Submit Request'}
            </button>
        </div>
    );
};

export default ConsultationRequest;
